import { LoginOutlined } from "@ant-design/icons";
import { Button, Table, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

const tagStyle = (tone) => {
  const tones = {
    idle: { color: "#707070", background: "#f3f4f6" },
    active: { color: "#144A94", background: "#e8eefb" },
    progress: { color: "#9a6b12", background: "#fff6e5" },
    done: { color: "#1f7a4d", background: "#e7f6ee" },
  };
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    ...tones[tone],
  };
};

function statusTone(value) {
  if (value === "pending") return "idle";
  if (value === "in_progress" || value === "translating") return "progress";
  if (value === "completed" || value === "complete" || value === "finalized") return "done";
  return "active";
}

function StatusTag({ status }) {
  const label = status?.label || "";
  return <span style={tagStyle(statusTone(status?.value))}>{label}</span>;
}

function formatDate(value) {
  if (!value) return "";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function GradeTable({ assignment }) {
  const history = useHistory();

  const openGrade = (grade) => {
    if (!grade.can_continue) return;
    const params = new URLSearchParams({
      assignmentId: String(assignment.id),
      gradeId: String(grade.id),
      competition: assignment.competition || "",
      grade: grade.grade || "",
      collection: grade.collection || "",
    });
    history.push(`/translate/paper?${params.toString()}`);
  };

  const columns = [
    {
      title: "Grade",
      dataIndex: "grade",
      key: "grade",
      render: (gradeName, grade) =>
        grade.can_continue ? (
          <Button
            type="link"
            style={{
              padding: 0,
              color: "#144A94",
              fontWeight: 650,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
            onClick={(event) => {
              event.stopPropagation();
              openGrade(grade);
            }}
          >
            {gradeName}
            <LoginOutlined aria-label="Open grade" />
          </Button>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {gradeName}
            <Tooltip title={grade.cannot_continue_reason || ""}>
              <span style={{ display: "inline-flex", color: "#E83D4D", cursor: "help" }}>
                <LoginOutlined aria-label={grade.cannot_continue_reason || "Cannot continue"} />
              </span>
            </Tooltip>
          </span>
        ),
    },
    { title: "Collection", dataIndex: "collection", key: "collection" },
    { title: "Questions", dataIndex: "total_questions", key: "total_questions" },
    { title: "Progress", dataIndex: "progress", key: "progress" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <StatusTag status={status} />,
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={assignment.grades || []}
      rowKey="id"
      pagination={false}
      size="middle"
      onRow={(grade) =>
        grade.can_continue
          ? { onClick: () => openGrade(grade), style: { cursor: "pointer" } }
          : {}
      }
    />
  );
}

export default function CompetitionTable({
  assignments,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
}) {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    setExpandedRowKeys(assignments[0] ? [assignments[0].id] : []);
  }, [assignments]);

  const columns = [
    { title: "Competition", dataIndex: "competition", key: "competition" },
    {
      title: "Start date",
      dataIndex: "from_date",
      key: "from_date",
      render: formatDate,
    },
    {
      title: "End date",
      dataIndex: "to_date",
      key: "to_date",
      render: formatDate,
    },
    {
      title: "Languages",
      dataIndex: "languages",
      key: "languages",
      render: (languages) => (languages || []).join(", "),
    },
    { title: "Progress", dataIndex: "progress", key: "progress" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <StatusTag status={status} />,
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={assignments}
      rowKey="id"
      loading={loading}
      expandable={{
        expandedRowKeys,
        onExpandedRowsChange: setExpandedRowKeys,
        expandedRowRender: (record) => <GradeTable assignment={record} />,
      }}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: false,
        onChange: onPageChange,
      }}
    />
  );
}
